import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash, FaUser, FaEnvelope } from "react-icons/fa";
import { DollarSign } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getMe,
    updateRibInfluenceur,
    updatePasswordInfluenceur
} from "../../services/InfluenceurService";
import * as LoadingIcons from "react-loading-icons";

const CompteInfluenceur = () => {
    const [user, setUser] = useState(null);
    const [iban, setIban] = useState("");
    const [isEditingIban, setIsEditingIban] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [stats, setStats] = useState([]);

    useEffect(() => {
        async function fetchData() {
            try {
                const me = await getMe();
                setUser(me);
                setIban(me.iban || "");

                // ⚡ Utiliser directement les valeurs retournées par getMe
                setStats([
                    { title: "Commission totale", value: me.commissions_total, color: "bg-yellow-100 text-yellow-600" },
                    { title: "Total payé", value: me.total_payee, color: "bg-green-100 text-green-600" },
                    { title: "Total non payé", value: me.total_non_payee, color: "bg-red-100 text-red-600" },
                ]);
            } catch (err) {
                console.error("Erreur lors du chargement du profil :", err);
                toast.error("Impossible de charger le profil");
            }
        }
        fetchData();
    }, []);

    const handleCopyLink = () => {
        const url = `https://gestionfreelance.com/register?promo=${user.code_promo}`;
        navigator.clipboard.writeText(url);
        toast.success("Lien copié ✅");
    };

    const handleSaveIban = async (e) => {
        e.preventDefault();
        try {
            await updateRibInfluenceur(iban);
            setUser(prev => ({ ...prev, iban }));
            setIsEditingIban(false);
            toast.success("IBAN mis à jour ✅");
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la mise à jour de l'IBAN");
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!oldPassword || !newPassword) {
            toast.error("Veuillez remplir les deux champs");
            return;
        }
        try {
            await updatePasswordInfluenceur(oldPassword, newPassword);
            setOldPassword("");
            setNewPassword("");
            toast.success("Mot de passe changé ✅");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Erreur lors de la mise à jour du mot de passe");
        }
    };

    if (!user) return (
        <div className="flex justify-center items-center h-screen">
            <LoadingIcons.SpinningCircles
                stroke="#2218eeff"
                fill="#2218eeff"
                strokeWidth={3}
                height={40}
                width={40}
            />
            <span className="ml-3 text-gray-600 font-medium">
                Chargement ...
            </span>
        </div>
    );

    return (
        <div className="bg-white shadow-lg rounded-lg pb-20 lg:p-8  text-left text-lg">
            <h2 className="lg:text-3xl text-2xl font-bold text-color6 mb-8">👤 Mon compte</h2>

            {/* Profil + Code Promo */}
            <div className="mb-8 flex flex-col lg:flex-row gap-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl lg:shadow-lg lg:p-6 p-4">
                <div className="flex-1 flex items-center lg:gap-6">
                    <div className="lg:flex hidden items-center justify-center w-24 h-24 rounded-full bg-color6 text-white text-4xl font-bold shadow-md">
                        {user.nom_complet.charAt(0)}
                    </div>
                    <div className="lg:space-y-3 space-y-1">
                        <div className="flex items-center gap-3">
                            <FaUser className="text-color6 text-2xl" />
                            <p className="text-xl font-semibold text-gray-800">{user.nom_complet}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <FaEnvelope className="text-color6 text-2xl" />
                            <p className="text-lg text-gray-700">{user.email}</p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 bg-green-50 rounded-2xl lg:shadow lg:p-6  flex flex-col items-start justify-center">
                    <h4 className="text-xl font-semibold text-green-600 mb-3">Code promo</h4>
                    <p className="lg:text-2xl text-md font-bold">{user.code_promo}</p>
                    <button
                        onClick={handleCopyLink}
                        className="lg:mt-4 mt-1 bg-color1 text-white lg:px-6 px-1 py-3 rounded-lg shadow hover:bg-color3 transition lg:text-lg text-sm"
                    >
                        Copier le lien de parrainage
                    </button>
                </div>
            </div>

            {/* Bloc Commissions KPI */}
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">💰 Mes commissions</h3>
                <div className="grid grid-cols-3 gap-4 lg:gap-6">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col items-center justify-between bg-white shadow rounded-2xl p-2 lg:p-3"
                        >
                            <div className={`p-2 lg:p-3 rounded-full ${stat.color}`}>
                                <DollarSign size={20} className="xl:size-10 lg:size-10" />
                            </div>
                            <div className="text-center mt-1">
                                <p className="text-xs lg:text-xl text-gray-800 font-semibold lg:font-semibold">
                                    {stat.title}
                                </p>
                                <h3 className="text-sm lg:text-3xl font-extrabold font-atkinson">
                                    {stat.value > 0 ? stat.value + " €" : "-"}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Section IBAN */}
            <div className="mb-8">
                <h3 className="lg:text-2xl text-lg font-semibold text-blue-600 mb-4">📤 IBAN</h3>
                {user.iban_masked && !isEditingIban ? (
                    <div className="flex lg:flex-col flex-row lg:gap-3 gap-2 items-start">
                        <p className="font-mono lg:text-xl text-base">{user.iban_masked}</p>
                        <button
                            onClick={() => setIsEditingIban(true)}
                            className="text-blue-600 underline hover:text-blue-800 lg:text-lg text-base"
                        >
                            Modifier
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSaveIban} className="flex flex-col gap-4 w-full max-w-md">
                        <input
                            type="text"
                            value={iban}
                            onChange={(e) => setIban(e.target.value)}
                            placeholder="FR..."
                            className="border rounded px-4 py-3 text-lg"
                            required
                        />
                        <div className="flex gap-4">
                            <button
                                type="submit"
                                className="bg-color3 text-white font-bold px-6 py-3 rounded-lg text-lg"
                            >
                                Sauvegarder
                            </button>
                            {user.iban_masked && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditingIban(false);
                                        setIban(user.iban_masked);
                                    }}
                                    className="bg-gray-400 text-white font-bold px-6 py-3 rounded-lg text-lg"
                                >
                                    Annuler
                                </button>
                            )}
                        </div>
                    </form>
                )}
            </div>

            {/* Section mot de passe */}
            <div className="mb-8">
                <h3 className="lg:text-2xl text-xl font-semibold text-color6 mb-4">🔒 Mot de passe</h3>
                <form onSubmit={handleChangePassword} className="flex flex-col gap-6 w-full max-w-md">
                    <div className="relative">
                        <label className="block mb-2 font-medium">Ancien mot de passe</label>
                        <input
                            type={showOldPassword ? "text" : "password"}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="w-full border rounded lg:px-4 px-3 lg:py-3 py-1 text-lg"
                        />
                        <span
                            className="absolute right-4 top-12 cursor-pointer text-xl"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                            {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>
                    <div className="relative">
                        <label className="block mb-2 font-medium">Nouveau mot de passe</label>
                        <input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full border rounded lg:px-4 px-3 lg:py-3 py-1 text-lg"
                        />
                        <span
                            className="absolute right-4 top-12 cursor-pointer text-xl"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>
                    <button
                        type="submit"
                        className="bg-color15 text-white font-bold rounded-lg lg:text-lg text-sm px-4 py-2 text-left flex items-center justify-center w-[120px] lg:w-auto hover:bg-color6 transition"
                    >
                        Modifier
                    </button>


                </form>
            </div>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default CompteInfluenceur;
